/**
 * WHAT: Hooks for safely executing asynchronous operations in React components
 *       (useMountedRef, useSafeState, useSafeAsync, useAsyncCallback).
 * WHY:  DAMA components frequently fetch data from the Django API. If a
 *       component unmounts before the response arrives (e.g., the user
 *       navigates away), calling setState on the unmounted component causes
 *       React warnings and potential memory leaks. These hooks guard every
 *       state update with a mounted-ref check and support AbortController
 *       so in-flight requests are cancelled on unmount.
 */

import { useEffect, useRef, useCallback, useState } from 'react';

/**
 * WHAT: Returns a ref whose `.current` is true while the component is mounted
 *       and false after unmount.
 * WHY:  Provides a synchronous, closure-safe way for async callbacks to check
 *       whether their component is still in the tree before calling setState.
 *
 * useMountedRef - A simple hook that tracks if component is mounted.
 * Useful for checking before setting state in async operations.
 *
 * @returns {Object} A ref object with .current being true while mounted
 *
 * @example
 * const isMounted = useMountedRef();
 *
 * const fetchData = async () => {
 *   const data = await api.getData();
 *   if (isMounted.current) {
 *     setData(data);
 *   }
 * };
 */
export function useMountedRef() {
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return isMountedRef;
}

/**
 * WHAT: Drop-in replacement for useState whose setter silently no-ops after
 *       the component unmounts.
 * WHY:  Eliminates "Can't perform a React state update on an unmounted
 *       component" warnings in any component that does async work without
 *       requiring manual mounted-ref checks at every call site.
 *
 * useSafeState - A useState wrapper that only updates state when mounted.
 * Drop-in replacement for useState in components with async operations.
 *
 * @param {any} initialValue - Initial state value
 * @returns {Array} [state, setSafeState] - Same interface as useState
 *
 * @example
 * const [data, setData] = useSafeState(null);
 * const [error, setError] = useSafeState(null);
 *
 * // These won't cause warnings if component unmounts during fetch
 * const fetchData = async () => {
 *   try {
 *     const result = await api.getData();
 *     setData(result);  // Safe - won't update if unmounted
 *   } catch (err) {
 *     setError(err);    // Safe - won't update if unmounted
 *   }
 * };
 */
export function useSafeState(initialValue) {
  const [state, setState] = useState(initialValue);
  const isMountedRef = useMountedRef();

  const setSafeState = useCallback((value) => {
    if (isMountedRef.current) {
      setState(value);
    }
  }, [isMountedRef]);

  return [state, setSafeState];
}

/**
 * WHAT: Executes an async function, tracks its loading/error/data state, and
 *       aborts in-flight requests on unmount or re-execution.
 * WHY:  Most DAMA pages fetch data on mount (applications, campaigns, user
 *       access). This hook replaces the manual useState+useEffect+try/catch
 *       pattern with a single call, and AbortController support ensures
 *       stale responses from slow API calls never overwrite newer data.
 *
 * useSafeAsync - A hook for safely executing async functions with automatic
 * cleanup and abort support.
 *
 * @param {Function} asyncFunction - Async function to execute
 * @param {Array} dependencies - Dependencies array (like useEffect)
 * @param {Object} options - Optional configuration
 * @param {boolean} options.immediate - Run immediately on mount (default: true)
 * @param {Function} options.onSuccess - Callback on success
 * @param {Function} options.onError - Callback on error
 *
 * @returns {Object} { data, error, loading, execute, abort }
 *
 * @example
 * // Basic usage
 * const { data, loading, error, execute } = useSafeAsync(
 *   () => api.fetchUsers(),
 *   []
 * );
 *
 * // With callbacks
 * const { execute } = useSafeAsync(
 *   () => api.saveData(formData),
 *   [formData],
 *   {
 *     immediate: false,
 *     onSuccess: (data) => showMessage('Saved!'),
 *     onError: (err) => showError(err.message)
 *   }
 * );
 */
export function useSafeAsync(asyncFunction, dependencies = [], options = {}) {
  const {
    immediate = true,
    onSuccess,
    onError
  } = options;

  const [state, setState] = useState({
    data: null,
    error: null,
    loading: immediate
  });

  const isMountedRef = useMountedRef();
  const abortControllerRef = useRef(null);

  const execute = useCallback(async (...args) => {
    // Abort any existing request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    if (isMountedRef.current) {
      setState(prev => ({ ...prev, loading: true, error: null }));
    }

    try {
      const data = await asyncFunction(...args, { signal });

      if (isMountedRef.current && !signal.aborted) {
        setState({ data, error: null, loading: false });
        if (onSuccess) onSuccess(data);
      }

      return data;
    } catch (error) {
      // Ignore abort errors
      if (error.name === 'AbortError') {
        return;
      }

      if (isMountedRef.current) {
        setState({ data: null, error, loading: false });
        if (onError) onError(error);
      }

      throw error;
    }
  }, [asyncFunction, onSuccess, onError, isMountedRef]);

  const abort = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
  }, []);

  // Run immediately if requested
  useEffect(() => {
    if (immediate) {
      execute();
    }

    return () => {
      // Abort on unmount
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  return {
    ...state,
    execute,
    abort
  };
}

/**
 * WHAT: Wraps an async function so its return value is discarded if the
 *       component has unmounted by the time the promise resolves.
 * WHY:  Event handlers like "Save" or "Delete" trigger API calls that may
 *       outlive the component. This wrapper prevents post-unmount side
 *       effects without requiring the caller to manage a mounted ref.
 *
 * useAsyncCallback - Creates a safe async callback that checks mount status.
 * Useful for event handlers that perform async operations.
 *
 * @param {Function} asyncCallback - The async function to wrap
 * @param {Array} dependencies - Dependencies for useCallback
 * @returns {Function} A wrapped async function that's safe to call
 *
 * @example
 * const handleSubmit = useAsyncCallback(async (formData) => {
 *   setLoading(true);
 *   try {
 *     await api.submit(formData);
 *     setMessage('Success!');
 *   } finally {
 *     setLoading(false);  // Safe - won't run if unmounted
 *   }
 * }, []);
 */
export function useAsyncCallback(asyncCallback, dependencies = []) {
  const isMountedRef = useMountedRef();
  const callbackRef = useRef(asyncCallback);

  useEffect(() => {
    callbackRef.current = asyncCallback;
  }, [asyncCallback]);

  return useCallback(async (...args) => {
    const result = await callbackRef.current(...args);
    // Only return result if still mounted
    if (isMountedRef.current) {
      return result;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
}

export default useSafeAsync;
